// packages/web/src/components/Layout/Footer.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const FooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(8, 0, 4, 0),
  borderTop: '1px solid #e5e5e5',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 0, 2, 0),
  },
}));

const FooterColumn = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
  },
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const FooterLink = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  color: '#666',
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'color 0.2s',
  fontWeight: 400,
  '&:hover': {
    color: '#84b817',
  },
}));

const Copyright = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 0, 3, 0),
  borderTop: '1px solid #e5e5e5',
  marginTop: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 0, 2, 0),
    marginTop: theme.spacing(2),
  },
}));

const SocialIconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2.5),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(1.5),
  },
}));

const SocialIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: '#e5e5e5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '& svg': {
    fontSize: 20,
    color: '#666',
  },
  '&:hover': {
    backgroundColor: '#84b817',
    transform: 'translateY(-2px)',
    '& svg': {
      color: '#fff',
    },
  },
}));

export const Footer: React.FC = () => {
  const navigate = useNavigate();

  return (
    <FooterSection>
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ 
          '@media (max-width: 600px)': { 
            spacing: 2 
          } 
        }}>
          <Grid item xs={6} sm={6} md={3}>
            <FooterColumn>
              <FooterTitle>Product</FooterTitle>
              <FooterLink onClick={() => navigate('/calendar')}>Features</FooterLink>
              <FooterLink onClick={() => navigate('/plans')}>Pricing</FooterLink>
              <FooterLink onClick={() => navigate('/courts')}>Find Courts</FooterLink>
            </FooterColumn>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <FooterColumn>
              <FooterTitle>Company</FooterTitle>
              <FooterLink  onClick={() => navigate('/about')}>About</FooterLink>
            </FooterColumn>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <FooterColumn>
              <FooterTitle>Support</FooterTitle>
              <FooterLink  onClick={() => navigate('/faq')}>FAQ</FooterLink>
              <FooterLink  onClick={() => navigate('/contacts')}>Contact Us</FooterLink>
            </FooterColumn>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <FooterColumn>
              <FooterTitle>Legal</FooterTitle>
              <FooterLink onClick={() => navigate('/privacy-policy')}>Privacy Policy</FooterLink>
              <FooterLink  onClick={() => navigate('/terms')}>Terms of Service</FooterLink>
            </FooterColumn>
          </Grid>
        </Grid>

        <Copyright>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem', fontWeight: 400 }}>
            © 2025 Sched Tennis. All rights reserved.
          </Typography>
          <SocialIconsContainer>
            <SocialIcon>
              <FacebookIcon />
            </SocialIcon>
            <SocialIcon>
              <TwitterIcon />
            </SocialIcon>
            <SocialIcon>
              <InstagramIcon />
            </SocialIcon>
            <SocialIcon>
              <LinkedInIcon />
            </SocialIcon>
          </SocialIconsContainer>
        </Copyright>
      </Container>
    </FooterSection>
  );
};